/**
 * useAuditLog Hook
 * ORCA360 - Audit logging for compliance
 *
 * Logs all assessment changes to audit_logs table
 * Captures user ID, timestamp, IP address, and change details
 *
 * Sprint 4, Day 4
 */

import { useCallback } from 'react';
import { supabase } from '../lib/supabase';

export type AuditAction =
  | 'assessment_created'
  | 'assessment_updated'
  | 'assessment_published'
  | 'assessment_acknowledged'
  | 'assessment_exported'
  | 'evidence_added'
  | 'evidence_updated'
  | 'evidence_deleted'
  | 'score_changed'
  | 'analysis_updated'
  | 'goals_updated'
  | 'debriefing_guide_updated'
  | 'debriefing_guide_finalized';

export interface AuditLogEntry {
  action: AuditAction;
  entity_type: 'assessment_pilot' | 'assessment_instructor' | 'evidence' | 'acknowledgment' | 'debriefing_guide';
  entity_id: string;
  changes?: Record<string, { old: any; new: any }>;
  metadata?: Record<string, any>;
}

export interface UseAuditLogReturn {
  /**
   * Log an action to the audit trail
   */
  logAction: (entry: AuditLogEntry) => Promise<void>;

  /**
   * Get audit log entries for a specific entity
   */
  getAuditLog: (entityType: string, entityId: string) => Promise<any[]>;

  /**
   * Get recent audit log entries
   */
  getRecentAuditLog: (limit?: number) => Promise<any[]>;
}

/**
 * Audit logging hook
 */
export function useAuditLog(): UseAuditLogReturn {
  /**
   * Log an action to the audit trail
   */
  const logAction = useCallback(async (entry: AuditLogEntry) => {
    try {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        console.warn('[useAuditLog] No authenticated user, skipping audit log');
        return;
      }

      // Prepare changes JSONB with metadata (including user_agent)
      const changesWithMetadata = entry.changes
        ? {
            ...entry.changes,
            _metadata: {
              ...entry.metadata,
              user_agent: navigator.userAgent,
            },
          }
        : {
            _metadata: {
              ...entry.metadata,
              user_agent: navigator.userAgent,
            },
          };

      // Call log_audit_entry RPC function (bypasses RLS with SECURITY DEFINER)
      // This function auto-fetches organization_id and uses server-side IP address
      const { error } = await supabase.rpc('log_audit_entry', {
        p_action: entry.action,
        p_entity_type: entry.entity_type,
        p_entity_id: entry.entity_id,
        p_changes: changesWithMetadata,
      });

      if (error) {
        console.error('[useAuditLog] Failed to log action:', error);
        throw error;
      }

      console.log('[useAuditLog] Action logged:', entry.action, entry.entity_id);
    } catch (err) {
      console.error('[useAuditLog] Error logging action:', err);
      // Don't throw - audit logging should not break the main flow
    }
  }, []);

  /**
   * Get audit log entries for a specific entity
   */
  const getAuditLog = useCallback(async (entityType: string, entityId: string) => {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(
          `
          *,
          user:user_id(
            email,
            role,
            full_name
          )
        `
        )
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('[useAuditLog] Failed to fetch audit log:', error);
        throw error;
      }

      return data || [];
    } catch (err) {
      console.error('[useAuditLog] Error fetching audit log:', err);
      return [];
    }
  }, []);

  /**
   * Get recent audit log entries
   */
  const getRecentAuditLog = useCallback(async (limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(
          `
          *,
          user:user_id(
            email,
            role,
            full_name
          )
        `
        )
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('[useAuditLog] Failed to fetch recent audit log:', error);
        throw error;
      }

      return data || [];
    } catch (err) {
      console.error('[useAuditLog] Error fetching recent audit log:', err);
      return [];
    }
  }, []);

  return {
    logAction,
    getAuditLog,
    getRecentAuditLog,
  };
}
